import { Reveal } from "../components/Reveal";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, RadialBarChart, RadialBar, Legend,
} from "recharts";
import { motion } from "framer-motion";

const WHITE = "#FFFFFF";
const MID = "#9A9A9A";
const DARK = "#4A4A4A";

const radar = [
  { attr: "Breathability", Linen: 98, Cotton: 82, Synthetic: 40 },
  { attr: "Durability", Linen: 92, Cotton: 78, Synthetic: 65 },
  { attr: "Softness", Linen: 74, Cotton: 95, Synthetic: 55 },
  { attr: "Moisture Wicking", Linen: 90, Cotton: 70, Synthetic: 45 },
  { attr: "Temp Regulation", Linen: 96, Cotton: 80, Synthetic: 38 },
  { attr: "Longevity", Linen: 94, Cotton: 76, Synthetic: 50 },
];

const temp = [
  { h: "6am", Linen: 20.5, Synthetic: 22.5 },
  { h: "10am", Linen: 21.2, Synthetic: 24.1 },
  { h: "2pm", Linen: 22.0, Synthetic: 26.8 },
  { h: "6pm", Linen: 21.6, Synthetic: 25.9 },
  { h: "10pm", Linen: 20.8, Synthetic: 24.2 },
  { h: "2am", Linen: 20.2, Synthetic: 23.0 },
];

const impact = [
  { name: "Water Use", Natural: 38, Synthetic: 100 },
  { name: "CO₂ Footprint", Natural: 44, Synthetic: 100 },
  { name: "Microplastics", Natural: 2, Synthetic: 100 },
  { name: "Biodegradable", Natural: 96, Synthetic: 8 },
];

const radial = [
  { name: "Absorbency", value: 92, fill: WHITE },
  { name: "Softness", value: 88, fill: MID },
  { name: "Durability", value: 94, fill: DARK },
];

const Stat = ({ n, l, delay }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ delay }} className="bg-black p-8">
    <div className="font-display text-5xl sm:text-6xl">{n}</div>
    <div className="util-link text-[10px] text-white/40 mt-3">{l}</div>
  </motion.div>
);

const Panel = ({ title, tag, children, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="border border-white/15 p-7 h-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl uppercase">{title}</h3>
        <span className="util-link text-[9px] text-white/40">{tag}</span>
      </div>
      {children}
    </div>
  </Reveal>
);

export default function MaterialScience() {
  return (
    <div data-testid="material-science-page" className="page-material-science bg-black text-white pt-16 min-h-screen">
      <div className="px-6 md:px-10 lg:px-20 pt-16 pb-28">
        <Reveal>
          <p className="util-link text-[11px] text-white/40 mb-4">Live Dashboard</p>
          <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] max-w-3xl">The Material Science</h1>
          <p className="text-white/50 mt-6 max-w-2xl leading-relaxed text-sm">
            Every fibre we weave is measured. Explore the real performance of natural cotton and linen
            against synthetic alternatives — breathability, durability, temperature and planet.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/15 mt-14">
          <Stat n="98%" l="Breathability index — linen" delay={0} />
          <Stat n="3×" l="Faster moisture wicking" delay={0.1} />
          <Stat n="+40%" l="Durability vs. synthetic" delay={0.2} />
          <Stat n="-62%" l="Water footprint" delay={0.3} />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Panel title="Fibre Performance" tag="Radar · Normalised">
            <ResponsiveContainer width="100%" height={340}>
              <RadarChart data={radar} outerRadius="72%">
                <PolarGrid stroke="rgba(255,255,255,0.15)" />
                <PolarAngleAxis dataKey="attr" tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }} />
                <Radar name="Linen" dataKey="Linen" stroke={WHITE} fill={WHITE} fillOpacity={0.4} />
                <Radar name="Cotton" dataKey="Cotton" stroke={MID} fill={MID} fillOpacity={0.25} />
                <Radar name="Synthetic" dataKey="Synthetic" stroke={DARK} fill={DARK} fillOpacity={0.15} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Temperature Regulation" tag="24h · °C" delay={0.1}>
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={temp}>
                <defs>
                  <linearGradient id="gL" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={WHITE} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={WHITE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="h" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} />
                <YAxis domain={[18, 28]} tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="Synthetic" stroke={DARK} fill={DARK} fillOpacity={0.2} strokeWidth={2} />
                <Area type="monotone" dataKey="Linen" stroke={WHITE} fill="url(#gL)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Environmental Impact" tag="Lower is better" delay={0.15}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={impact} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 10 }} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Natural" fill={WHITE} />
                <Bar dataKey="Synthetic" fill={DARK} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title="Towel Metrics" tag="Radial · %" delay={0.2}>
            <ResponsiveContainer width="100%" height={340}>
              <RadialBarChart data={radial} innerRadius="30%" outerRadius="95%" startAngle={90} endAngle={-270}>
                <RadialBar background={{ fill: "rgba(255,255,255,0.08)" }} dataKey="value" />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </RadialBarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      </div>
    </div>
  );
}
